
import { useState } from 'react';
import { supabase } from '../../../lib/supabase';

interface AuthFormProps {
  activeTab: 'login' | 'signup';
}

export default function AuthForm({ activeTab }: AuthFormProps) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const resetForm = () => {
    setError('');
    setSuccess('');
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    resetForm();
    setLoading(true);

    try {
      if (!email || !password) {
        throw new Error('Please enter both email and password.');
      }

      const { data, error: signInError } = await supabase.auth.signInWithPassword({
        email: email.trim().toLowerCase(),
        password,
      });

      if (signInError) {
        if (
          signInError.message.includes('Invalid login credentials') ||
          signInError.message.includes('Invalid')
        ) {
          throw new Error('Invalid email or password. Please try again.');
        } else if (signInError.message.includes('Email not confirmed')) {
          throw new Error('Please verify your email address before signing in.');
        } else {
          throw new Error('Unable to sign in. Please check your credentials.');
        }
      }

      if (data.session) {
        // Navigate programmatically – replace with your router’s navigation method
        // Example for Next.js: router.push('/admin');
        // Here we use a safe fallback to avoid runtime errors if the global is undefined
        if (typeof window !== 'undefined' && typeof (window as any).REACT_APP_NAVIGATE === 'function') {
          (window as any).REACT_APP_NAVIGATE('/admin');
        }
      }
    } catch (err: any) {
      setError(err.message || 'An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    resetForm();
    setLoading(true);

    try {
      if (!fullName || !email || !password || !confirmPassword) {
        throw new Error('Please fill in all fields.');
      }

      if (password.length < 6) {
        throw new Error('Password must be at least 6 characters.');
      }

      if (password !== confirmPassword) {
        throw new Error('Passwords do not match.');
      }

      const { error: signUpError } = await supabase.auth.signUp({
        email: email.trim().toLowerCase(),
        password,
        options: {
          data: { full_name: fullName.trim() },
        },
      });

      if (signUpError) {
        if (signUpError.message.includes('already registered')) {
          throw new Error('This email is already registered. Please sign in instead.');
        } else {
          throw new Error(signUpError.message);
        }
      }

      setSuccess(
        'Account created successfully! Please check your email to verify your account, then sign in.'
      );
      setEmail('');
      setPassword('');
      setFullName('');
      setConfirmPassword('');
    } catch (err: any) {
      setError(err.message || 'An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <h1 className="text-2xl font-bold text-slate-900 mb-1">
        {activeTab === 'login' ? 'Sign in to your account' : 'Create a new account'}
      </h1>
      <p className="text-sm text-slate-500 mb-8">
        {activeTab === 'login'
          ? 'Enter your credentials to access the admin dashboard.'
          : 'Fill in your details to get started.'}
      </p>

      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-3.5 flex items-start gap-2.5 animate-[slideUp_0.2s_ease-out]">
          <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
            <i className="ri-error-warning-fill text-red-500"></i>
          </div>
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {success && (
        <div className="mb-6 bg-emerald-50 border border-emerald-200 rounded-lg p-3.5 flex items-start gap-2.5 animate-[slideUp_0.2s_ease-out]">
          <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
            <i className="ri-checkbox-circle-fill text-emerald-500"></i>
          </div>
          <p className="text-sm text-emerald-700">{success}</p>
        </div>
      )}

      {activeTab === 'login' ? (
        <form onSubmit={handleLogin} className="space-y-5">
          <div>
            <label htmlFor="login-email" className="block text-sm font-medium text-slate-700 mb-1.5">
              Email
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-mail-line text-slate-400 text-base"></i>
                </div>
              </div>
              <input
                id="login-email"
                type="email"
                name="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  resetForm();
                }}
                required
                autoComplete="email"
                placeholder="you@example.com"
                className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-lg text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/40 focus:border-emerald-500 transition-all"
              />
            </div>
          </div>

          <div>
            <label htmlFor="login-password" className="block text-sm font-medium text-slate-700 mb-1.5">
              Password
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-lock-line text-slate-400 text-base"></i>
                </div>
              </div>
              <input
                id="login-password"
                type={showPassword ? 'text' : 'password'}
                name="password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  resetForm();
                }}
                required
                autoComplete="current-password"
                placeholder="Enter your password"
                className="w-full pl-10 pr-11 py-3 border border-slate-200 rounded-lg text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/40 focus:border-emerald-500 transition-all"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute inset-y-0 right-0 pr-3.5 flex items-center cursor-pointer"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i
                    className={`${
                      showPassword ? 'ri-eye-off-line' : 'ri-eye-line'
                    } text-slate-400 hover:text-slate-600 text-base transition-colors`}
                  ></i>
                </div>
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-medium rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <i className="ri-loader-4-line animate-spin"></i>
                Signing in...
              </span>
            ) : (
              'Sign In'
            )}
          </button>
        </form>
      ) : (
        <form onSubmit={handleSignup} className="space-y-5">
          <div>
            <label htmlFor="signup-name" className="block text-sm font-medium text-slate-700 mb-1.5">
              Full Name
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-user-line text-slate-400 text-base"></i>
                </div>
              </div>
              <input
                id="signup-name"
                type="text"
                name="fullName"
                value={fullName}
                onChange={(e) => {
                  setFullName(e.target.value);
                  resetForm();
                }}
                required
                autoComplete="name"
                placeholder="John Doe"
                className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-lg text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/40 focus:border-emerald-500 transition-all"
              />
            </div>
          </div>

          <div>
            <label htmlFor="signup-email" className="block text-sm font-medium text-slate-700 mb-1.5">
              Email
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-mail-line text-slate-400 text-base"></i>
                </div>
              </div>
              <input
                id="signup-email"
                type="email"
                name="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  resetForm();
                }}
                required
                autoComplete="email"
                placeholder="you@example.com"
                className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-lg text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/40 focus:border-emerald-500 transition-all"
              />
            </div>
          </div>

          <div>
            <label htmlFor="signup-password" className="block text-sm font-medium text-slate-700 mb-1.5">
              Password
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-lock-line text-slate-400 text-base"></i>
                </div>
              </div>
              <input
                id="signup-password"
                type={showPassword ? 'text' : 'password'}
                name="password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  resetForm();
                }}
                required
                autoComplete="new-password"
                placeholder="Min. 6 characters"
                className="w-full pl-10 pr-11 py-3 border border-slate-200 rounded-lg text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/40 focus:border-emerald-500 transition-all"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute inset-y-0 right-0 pr-3.5 flex items-center cursor-pointer"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i
                    className={`${
                      showPassword ? 'ri-eye-off-line' : 'ri-eye-line'
                    } text-slate-400 hover:text-slate-600 text-base transition-colors`}
                  ></i>
                </div>
              </button>
            </div>
          </div>

          <div>
            <label htmlFor="signup-confirm" className="block text-sm font-medium text-slate-700 mb-1.5">
              Confirm Password
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-lock-check-line text-slate-400 text-base"></i>
                </div>
              </div>
              <input
                id="signup-confirm"
                type={showPassword ? 'text' : 'password'}
                name="confirmPassword"
                value={confirmPassword}
                onChange={(e) => {
                  setConfirmPassword(e.target.value);
                  resetForm();
                }}
                required
                autoComplete="new-password"
                placeholder="Re-enter your password"
                className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-lg text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/40 focus:border-emerald-500 transition-all"
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-medium rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <i className="ri-loader-4-line animate-spin"></i>
                Creating account...
              </span>
            ) : (
              'Create Account'
            )}
          </button>
        </form>
      )}

      <p className="text-xs text-slate-400 text-center mt-8">
        By continuing, you agree to Greenify’s Terms of Service and Privacy Policy.
      </p>
    </div>
  );
}
